import React, { useMemo, useState } from "react";
import moment from "moment";
import ReportPageShell from "../components/ReportPageShell";
import { defaultFilters, toQueryParams } from "../shared/filters";
import { useDetailQuery } from "../shared/useDetailQuery";
import { useSearchGate } from "../shared/useSearchGate";
import { centsToUsd, formatCount } from "../shared/format";
import {
  CommissaryListResponse,
  CommissaryOrderRow,
  CommissaryVendorTotal,
} from "../api/types";

const LIMIT_OPTIONS = [500, 2000, 10000];
const DEFAULT_LIMIT = 2000;

const TOOLTIP =
  "Commissary (RP-2173) — one row per commissary order, grouped by vendor, matching the legacy Commissary " +
  "Report. Every workflow status is listed except manual payout adjustments (excluded, like legacy). " +
  "Money columns are the raw stored amounts (commissary is not a sale — it never appears in the sales " +
  "reports). Vendor card totals sum FULFILLED orders only, computed server-side, exactly like the legacy page.";

// Legacy formatStatus: "pending_approval" -> "Pending Approval".
const formatStatus = (status: string) =>
  status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// Commissary report page (RP-2173) — CommissaryList via the new engine, keeping the legacy
// vendor-card layout and headers: vendor header (orders · POC · Subtotal / Tax / Total), expandable
// order rows (Order # · status · Placed by · Items / Subtotal / Tax / Total), and the line-item
// table (Item / Qty / Unit Price / Line Total / Notes) plus the notes blocks and fulfilled time.
export default function CommissaryReport() {
  const [filters, setFilters] = useState(defaultFilters);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const [vendorSearch, setVendorSearch] = useState("");
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const { submitted, isDirty, hasRun, search } = useSearchGate({ filters, limit });
  const state = useDetailQuery(
    submitted
      ? toQueryParams("CommissaryList", submitted.filters, { limit: submitted.limit })
      : null
  );

  const toggleOrder = (id: string) =>
    setExpandedOrderId((prev) => (prev === id ? null : id));

  return (
    <ReportPageShell
      title="Commissary"
      tooltipText={TOOLTIP}
      filters={filters}
      onFiltersChange={setFilters}
      state={state}
      onSearch={search}
      isDirty={isDirty}
      hasRun={hasRun}
      filterVendor
      actions={
        <>
          <input
            type="text"
            value={vendorSearch}
            onChange={(e) => setVendorSearch(e.target.value)}
            placeholder="Search by vendor name..."
            className="w-64 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-600"
          />
          <label className="flex items-center gap-1 text-sm text-gray-600">
            Row limit
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="rounded-md border border-gray-300 px-2 py-1 text-sm"
            >
              {LIMIT_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n.toLocaleString()}
                </option>
              ))}
            </select>
          </label>
        </>
      }
    >
      {(data) => (
        <CommissaryBody
          data={data as CommissaryListResponse}
          vendorSearch={vendorSearch}
          expandedOrderId={expandedOrderId}
          onToggleOrder={toggleOrder}
        />
      )}
    </ReportPageShell>
  );
}

function CommissaryBody({
  data,
  vendorSearch,
  expandedOrderId,
  onToggleOrder,
}: {
  data: CommissaryListResponse;
  vendorSearch: string;
  expandedOrderId: string | null;
  onToggleOrder: (id: string) => void;
}) {
  // Group rows under the SERVER-computed vendor totals (already in legacy vendor-name order).
  // The page never sums money — it renders what the engine shipped.
  const groups = useMemo(() => {
    const rowsByVendor = new Map<string, CommissaryOrderRow[]>();
    for (const row of data.rows) {
      const list = rowsByVendor.get(row.vendorId) ?? [];
      list.push(row);
      rowsByVendor.set(row.vendorId, list);
    }
    const q = vendorSearch.trim().toLowerCase();
    return data.vendorTotals
      .filter((v) => !q || v.vendorName.toLowerCase().includes(q))
      .map((v) => ({ vendor: v, orders: rowsByVendor.get(v.vendorId) ?? [] }));
  }, [data, vendorSearch]);

  return (
    <div className="space-y-3">
      {/* Grand totals — server-computed; money is FULFILLED-only, like the legacy vendor cards. */}
      <div className="flex flex-wrap items-center gap-6 rounded-md border border-gray-200 bg-white px-4 py-3">
        <Stat label="Orders" value={formatCount(data.totals.OrderCount)} />
        <Stat label="Subtotal (fulfilled)" value={centsToUsd(data.moneyTotals.fulfilledSubtotal)} />
        <Stat label="Tax (fulfilled)" value={centsToUsd(data.moneyTotals.fulfilledTax)} />
        <Stat label="Total (fulfilled)" value={centsToUsd(data.moneyTotals.fulfilledTotal)} bold />
        {data.truncated && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
            Rows capped — totals cover the full set; raise the row limit to see every order
          </span>
        )}
      </div>

      {groups.length === 0 && (
        <div className="rounded-md bg-white px-4 py-10 text-center text-sm text-gray-500">
          No commissary orders found.
        </div>
      )}

      {groups.map(({ vendor, orders }) => (
        <VendorCard
          key={vendor.vendorId}
          vendor={vendor}
          orders={orders}
          expandedOrderId={expandedOrderId}
          onToggleOrder={onToggleOrder}
        />
      ))}
    </div>
  );
}

function VendorCard({
  vendor,
  orders,
  expandedOrderId,
  onToggleOrder,
}: {
  vendor: CommissaryVendorTotal;
  orders: CommissaryOrderRow[];
  expandedOrderId: string | null;
  onToggleOrder: (id: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm transition-colors hover:border-primary-500">
      {/* Vendor header — legacy card header: name, order count, POC, Subtotal / Tax / Total */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3">
        <div>
          <div className="text-base font-semibold text-gray-900">{vendor.vendorName}</div>
          <div className="text-xs text-gray-400">
            {vendor.orderCount} order{vendor.orderCount !== 1 ? "s" : ""}
            {vendor.pocName && (
              <>
                {" · "}
                {vendor.pocName}
                {vendor.pocPhone && ` (${vendor.pocPhone})`}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-6 text-sm">
          <Stat label="Subtotal" value={centsToUsd(vendor.fulfilledSubtotal)} />
          <Stat label="Tax" value={centsToUsd(vendor.fulfilledTax)} />
          <Stat label="Total" value={centsToUsd(vendor.fulfilledTotal)} bold />
        </div>
      </div>

      {/* Order rows */}
      <div className="divide-y divide-gray-100">
        {orders.map((order) => {
          const isExpanded = expandedOrderId === order.orderId;
          return (
            <div key={order.orderId}>
              <button
                onClick={() => onToggleOrder(order.orderId)}
                className="flex w-full cursor-pointer items-center justify-between px-4 py-2.5 transition-colors hover:bg-gray-50"
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <svg
                    className={`h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-transform ${
                      isExpanded ? "rotate-90" : ""
                    }`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="min-w-0 text-left">
                    <div className="truncate text-sm font-medium text-gray-700">
                      Order #{order.legacyId ?? order.orderId.slice(0, 8)}
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        {formatStatus(order.status)}
                      </span>
                    </div>
                    {order.placedBy && (
                      <div className="text-[11px] text-gray-500">
                        <span className="mr-1 uppercase tracking-wide text-gray-400">
                          Placed by:
                        </span>
                        {order.placedBy}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-5 text-xs">
                  <Stat label="Items" value={String(order.items.length)} small />
                  <Stat label="Subtotal" value={centsToUsd(order.subtotal)} small />
                  <Stat label="Tax" value={centsToUsd(order.tax)} small />
                  <Stat label="Total" value={centsToUsd(order.total)} small bold />
                </div>
              </button>

              {isExpanded && (
                <div className="border-b-2 border-t-2 border-gray-300 border-l-4 border-l-primary-700 bg-gray-100 px-5 py-4 shadow-inner">
                  <OrderLineItems order={order} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OrderLineItems({ order }: { order: CommissaryOrderRow }) {
  return (
    <div className="space-y-3">
      {order.items.length === 0 ? (
        <div className="text-sm italic text-gray-400">No line items on this order.</div>
      ) : (
        <div className="overflow-hidden rounded-md border border-gray-300 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-800 text-[10px] uppercase text-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-semibold tracking-wide">Item</th>
                <th className="px-3 py-2 text-right font-semibold tracking-wide">Qty</th>
                <th className="px-3 py-2 text-right font-semibold tracking-wide">Unit Price</th>
                <th className="px-3 py-2 text-right font-semibold tracking-wide">Line Total</th>
                <th className="px-3 py-2 text-left font-semibold tracking-wide">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {order.items.map((item, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="px-3 py-2 font-medium text-gray-800">{item.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                    {item.quantity}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                    {centsToUsd(item.unitPrice)}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums text-gray-900">
                    {centsToUsd(item.lineTotal)}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{item.notes ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(order.notes || order.organizerNotes || order.rejectionReason) && (
        <div className="grid grid-cols-1 gap-3 text-xs md:grid-cols-3">
          {order.notes && <NoteBlock label="Vendor Notes" value={order.notes} />}
          {order.organizerNotes && (
            <NoteBlock label="Organizer Notes" value={order.organizerNotes} />
          )}
          {order.rejectionReason && (
            <NoteBlock label="Rejection Reason" value={order.rejectionReason} />
          )}
        </div>
      )}

      {order.fulfilledAt && (
        <div className="text-[11px] text-gray-400">
          Fulfilled: {moment(order.fulfilledAt).format("MMM DD, YYYY h:mm a")}
        </div>
      )}
    </div>
  );
}

const NoteBlock = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-md border border-gray-200 bg-white p-2">
    <div className="mb-0.5 text-[9px] font-medium uppercase text-gray-400">{label}</div>
    <div className="text-gray-700">{value}</div>
  </div>
);

const Stat = ({
  label,
  value,
  bold,
  small,
}: {
  label: string;
  value: string;
  bold?: boolean;
  small?: boolean;
}) => (
  <div className="text-right">
    <div className={`uppercase text-gray-400 ${small ? "text-[9px]" : "text-[10px]"}`}>
      {label}
    </div>
    <div
      className={`tabular-nums ${bold ? "font-semibold text-gray-900" : "text-gray-700"} ${
        small ? "text-xs" : ""
      }`}
    >
      {value}
    </div>
  </div>
);
